import { ReactElement } from 'react';
import { getConfig } from '~/org/twilio/one-support-center/shared/osc-env';
import { Box } from '@twilio-paste/core/box';
import { Text } from '@twilio-paste/core/text';
import { Topbar, TopbarActions } from '@twilio-paste/core/topbar';
import {
  UserDialog,
  UserDialogContainer,
  UserDialogList,
  UserDialogListItem,
  UserDialogUserInfo,
  UserDialogUserName,
  UserDialogUserEmail,
  useUserDialogListState,
} from '@twilio-paste/core/user-dialog';
import {
  ProductSwitcher,
  ProductSwitcherButton,
  ProductSwitcherItem,
  useProductSwitcherState,
} from '@twilio-paste/core/product-switcher';
import { LogOutIcon } from '@twilio-paste/icons/esm/LogOutIcon';
import { UserIcon } from '@twilio-paste/icons/esm/UserIcon';
import { LogoTwilioIcon } from '@twilio-paste/icons/esm/LogoTwilioIcon';

import AccountSwitcherMenu from './accountSwitcherMenu';
import AiNavButton from './aiNavButton';
import SupportPlanMenuPopover from './supportPlanMenuPopover';
import SystemStatusMenuIcon from './systemStatusMenuIcon';
import { constants, copy } from '../definitions';
import { useUserDetails } from '../../../hooks';
import { logoutUrl } from '../../../utils';
import styles from '../layout.module.css';

const CompositeTopbar = (): ReactElement => {
  const { user } = useUserDetails();
  const { consoleEndpoint } = getConfig();

  const userDialogList = useUserDialogListState();
  const productSwitcher = useProductSwitcherState();

  if (user) {
    const userFullName = `${user.firstName} ${user.lastName}`;

    return (
      <Box as="div" gridArea="topbar" className={styles.topbarContainer}>
        <Topbar id={constants.navigationSkipLinks.topbar}>
          <TopbarActions justify="start">
            <Box as="div" className={styles.topbarMenusContainer}>
              <AccountSwitcherMenu />

              <Box as="span" display="flex" columnGap="space30">
                <SupportPlanMenuPopover />
              </Box>
            </Box>
          </TopbarActions>

          <TopbarActions justify="end">
            <UserDialogContainer
              name={copy.topbar.userDialog.name}
              icon={UserIcon}
            >
              <AiNavButton />
              <SystemStatusMenuIcon />
              <Box as="div" display="flex" columnGap="space20">
                <ProductSwitcherButton
                  {...productSwitcher}
                  i18nButtonLabel="Switch products"
                />
                <Text as="span" color="colorTextIcon" fontSize="fontSize30">
                  {copy.topbar.switchProductsButton}
                </Text>
              </Box>
              <ProductSwitcher
                {...productSwitcher}
                aria-label="Available Products"
              >
                <ProductSwitcherItem
                  {...productSwitcher}
                  name="product"
                  value="twilio"
                  onChange={(): void => {
                    window.location.replace(consoleEndpoint);
                  }}
                  productName="Twilio Comms"
                  productStrapline="SMS, Voice, & Video"
                  productIcon={<LogoTwilioIcon decorative />}
                />
              </ProductSwitcher>
              <UserDialog aria-label={copy.topbar.userDialog.name}>
                <UserDialogUserInfo>
                  <UserDialogUserName>{userFullName}</UserDialogUserName>
                  <UserDialogUserEmail>
                    {user?.emailAddr || ''}
                  </UserDialogUserEmail>
                </UserDialogUserInfo>

                <UserDialogList
                  {...userDialogList}
                  aria-label={copy.topbar.userDialog.actions}
                >
                  <UserDialogListItem
                    {...userDialogList}
                    selected={false}
                    href={logoutUrl}
                  >
                    <LogOutIcon decorative />
                    {copy.topbar.userDialog.logout}
                  </UserDialogListItem>
                </UserDialogList>
              </UserDialog>
            </UserDialogContainer>
          </TopbarActions>
        </Topbar>
      </Box>
    );
  }

  return <div />;
};

export default CompositeTopbar;
